import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { API_URL } from '../../lib/constants';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';

const STYLES = ['socratic', 'didactic', 'narrative', 'exemplary', 'counseling'];
const ANCHOR_SOURCES = ['scripture', 'founder_texts', 'personal_history', 'medical_lit', 'none'];
const PACINGS = ['gradual', 'direct', 'responsive_only'];
const VOICES = ['companion', 'mentor', 'teacher', 'peer', 'pastor'];
const QUESTION_DENSITIES = ['high', 'medium', 'low'];
const PERSONALIZATION_DEPTHS = ['none', 'low', 'medium', 'high'];

const defaultMethodology = () => ({
  style: 'socratic',
  anchorSource: 'scripture',
  pacing: 'responsive_only',
  voice: 'companion',
  questionDensity: 'high',
  personalizationDepth: 'medium',
});

const emptyForm = () => ({
  id: '',
  name: '',
  description: '',
  methodology: defaultMethodology(),
});

export default function MethodologyPresetPage() {
  const { userInfo } = useUserInfoContext();
  const [presets, setPresets] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());

  const authHeaders = {
    Authorization: `Bearer ${userInfo?.accessToken}`,
  };
  const jsonHeaders = {
    ...authHeaders,
    'Content-Type': 'application/json',
  };

  const load = () => {
    if (!userInfo?.accessToken) return;
    fetch(`${API_URL}/methodology-presets`, { headers: authHeaders })
      .then((r) => r.json())
      .then((data) => setPresets(Array.isArray(data) ? data : []));
  };

  useEffect(load, [userInfo?.accessToken]);

  const startCreate = () => {
    setSelectedId(null);
    setForm(emptyForm());
  };

  const startEdit = (preset: any) => {
    setSelectedId(preset.id);
    setForm({
      ...emptyForm(),
      ...preset,
      methodology: { ...defaultMethodology(), ...(preset.methodology || {}) },
    });
  };

  const save = async () => {
    const isNew = !selectedId;
    const url = isNew
      ? `${API_URL}/methodology-presets`
      : `${API_URL}/methodology-presets/${selectedId}`;
    const method = isNew ? 'POST' : 'PUT';
    const res = await fetch(url, { method, headers: jsonHeaders, body: JSON.stringify(form) });
    if (!res.ok) {
      const err = await res.json();
      alert(err.error || 'Save failed');
      return;
    }
    const saved = await res.json();
    load();
    if (isNew && saved?.id) {
      startEdit(saved);
    }
  };

  const remove = async (presetId: string, presetName?: string) => {
    const label = presetName ? `"${presetName}"` : 'this preset';
    if (!window.confirm(`Delete ${label}? Goals linked to it will fall back to their embedded methodology snapshot.`)) {
      return;
    }
    await fetch(`${API_URL}/methodology-presets/${presetId}`, { method: 'DELETE', headers: authHeaders });
    if (selectedId === presetId) {
      startCreate();
    }
    load();
  };

  const m = form.methodology;
  const setM = (field: string, value: string) =>
    setForm({ ...form, methodology: { ...form.methodology, [field]: value } });

  return (
    <Box sx={{ p: 2, display: 'flex', gap: 2, height: '100%' }}>
      <Paper sx={{ width: 360, p: 2, overflow: 'auto' }}>
        <Typography variant="h6" gutterBottom>Methodology presets</Typography>
        <Button fullWidth variant="outlined" sx={{ mb: 2 }} onClick={startCreate}>
          New preset
        </Button>
        {presets.map((p) => (
          <Box
            key={p.id}
            sx={{
              p: 1,
              mb: 1,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.5,
              bgcolor: selectedId === p.id ? 'action.selected' : 'transparent',
              borderRadius: 1,
            }}
          >
            <Box
              sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
              onClick={() => startEdit(p)}
            >
              <Typography variant="subtitle2" noWrap>{p.name}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                <Chip size="small" label={p.methodology?.style} />
                <Chip size="small" label={p.methodology?.voice} />
                <Chip size="small" label={p.methodology?.pacing} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexShrink: 0 }}>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => startEdit(p)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={() => remove(p.id, p.name)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Paper>
      <Paper sx={{ flex: 1, p: 2, overflow: 'auto' }}>
        <Typography variant="h6" gutterBottom>{selectedId ? 'Edit preset' : 'Create preset'}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="ID (optional on create)"
              fullWidth
              value={form.id}
              disabled={Boolean(selectedId)}
              helperText={selectedId ? 'ID cannot be changed after create' : undefined}
              onChange={(e) => setForm({ ...form, id: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Name" fullWidth value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Description" fullWidth multiline minRows={2} value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Methodology fields</Typography>
            <Typography variant="caption" color="text.secondary">
              These settings control how the avatar converses when pursuing a linked goal.
            </Typography>
          </Grid>
          <Grid item xs={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Style</InputLabel>
              <Select label="Style" value={m.style} onChange={(e) => setM('style', e.target.value)}>
                {STYLES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Anchor source</InputLabel>
              <Select label="Anchor source" value={m.anchorSource} onChange={(e) => setM('anchorSource', e.target.value)}>
                {ANCHOR_SOURCES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Pacing</InputLabel>
              <Select label="Pacing" value={m.pacing} onChange={(e) => setM('pacing', e.target.value)}>
                {PACINGS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Voice</InputLabel>
              <Select label="Voice" value={m.voice} onChange={(e) => setM('voice', e.target.value)}>
                {VOICES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Question density</InputLabel>
              <Select label="Question density" value={m.questionDensity} onChange={(e) => setM('questionDensity', e.target.value)}>
                {QUESTION_DENSITIES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Personalization</InputLabel>
              <Select label="Personalization" value={m.personalizationDepth} onChange={(e) => setM('personalizationDepth', e.target.value)}>
                {PERSONALIZATION_DEPTHS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="contained" onClick={save}>
            {selectedId ? 'Save changes' : 'Create preset'}
          </Button>
          {selectedId && (
            <>
              <Button variant="outlined" onClick={startCreate}>Cancel</Button>
              <Button variant="outlined" color="error" onClick={() => remove(selectedId, form.name)}>
                Delete
              </Button>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
